import * as ipaddr from 'ipaddr.js';
import { z } from 'zod';
import { ipRangeToCidrs } from './ip-range-cidr.utils';

export const IP_ALLOWLIST_MAX_ENTRIES = 500;
export const IP_ALLOWLIST_DESCRIPTION_MAX_LENGTH = 200;
export const IP_ALLOWLIST_DENIED_CODE = 'IP_NOT_ALLOWED';
export const IP_ALLOWLIST_DENIED_EVENT = 'terra:ip-allowlist-denied';

const IPV4_BITS = 32;
const IPV6_BITS = 128;

const isIpv6Address = (
  address: ipaddr.IPv4 | ipaddr.IPv6,
): address is ipaddr.IPv6 => address.kind() === 'ipv6';

export const parseIpAddress = (
  value: string,
): ipaddr.IPv4 | ipaddr.IPv6 | null => {
  try {
    const parsed = ipaddr.parse(value.trim());

    if (isIpv6Address(parsed) && parsed.isIPv4MappedAddress()) {
      return parsed.toIPv4Address();
    }

    return parsed;
  } catch {
    return null;
  }
};

const toNetworkCidr = ({
  address,
  prefix,
}: {
  address: ipaddr.IPv4 | ipaddr.IPv6;
  prefix: number;
}): string => {
  const bits = address.kind() === 'ipv4' ? IPV4_BITS : IPV6_BITS;

  if (prefix < 1 || prefix > bits) {
    throw new Error('Invalid prefix length');
  }

  const bytes = address.toByteArray();
  let remaining = prefix;
  const masked = bytes.map((byte) => {
    if (remaining >= 8) {
      remaining -= 8;

      return byte;
    }

    if (remaining <= 0) {
      return 0;
    }

    const mask = (0xff << (8 - remaining)) & 0xff;

    remaining = 0;

    return byte & mask;
  });

  return `${ipaddr.fromByteArray(masked).toString()}/${prefix}`;
};

export const normalizeCidr = (value: string): string | null => {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  try {
    if (trimmed.includes('/')) {
      const [address, prefix] = ipaddr.parseCIDR(trimmed);

      return toNetworkCidr({ address, prefix });
    }

    const address = parseIpAddress(trimmed);

    if (!address) {
      return null;
    }

    const prefix = address.kind() === 'ipv4' ? IPV4_BITS : IPV6_BITS;

    return `${address.toString()}/${prefix}`;
  } catch {
    return null;
  }
};

export type ParsedIpAllowlistInput = {
  cidrs: string[];
  invalid: string[];
};

const IP_ALLOWLIST_INPUT_SEPARATOR = /[\s,;]+/;

export const hasMultipleIpAllowlistValues = (text: string): boolean =>
  text.trim().includes('-') || IP_ALLOWLIST_INPUT_SEPARATOR.test(text.trim());

const expandIpAllowlistToken = (token: string): string[] | null => {
  const [firstIp, lastIp, ...rest] = token.split('-');

  if (lastIp === undefined) {
    const cidr = normalizeCidr(token);

    return cidr ? [cidr] : null;
  }

  if (rest.length > 0 || !firstIp || !lastIp) {
    return null;
  }

  const { cidrs, truncated } = ipRangeToCidrs({ firstIp, lastIp });
  const normalized = cidrs.map(normalizeCidr);

  if (truncated || normalized.length === 0) {
    return null;
  }

  return normalized.every((cidr): cidr is string => cidr !== null)
    ? normalized
    : null;
};

export const parseIpAllowlistInput = (text: string): ParsedIpAllowlistInput => {
  const parsed = text
    .replace(/\s*-\s*/g, '-')
    .split(IP_ALLOWLIST_INPUT_SEPARATOR)
    .filter((token) => token.length > 0)
    .reduce<ParsedIpAllowlistInput>(
      (result, token) => {
        const cidrs = expandIpAllowlistToken(token);

        return cidrs
          ? { ...result, cidrs: [...result.cidrs, ...cidrs] }
          : { ...result, invalid: [...result.invalid, token] };
      },
      { cidrs: [], invalid: [] },
    );

  return { ...parsed, cidrs: Array.from(new Set(parsed.cidrs)) };
};

export const IpAllowlistCidrSchema = z
  .string()
  .trim()
  .transform((value, ctx) => {
    const normalized = normalizeCidr(value);

    if (!normalized) {
      ctx.addIssue({
        code: 'custom',
        message: 'Enter a valid IP address or CIDR',
      });

      return z.NEVER;
    }

    return normalized;
  });

export const IpAllowlistEntrySchema = z.object({
  cidr: IpAllowlistCidrSchema,
  description: z
    .string()
    .trim()
    .max(IP_ALLOWLIST_DESCRIPTION_MAX_LENGTH)
    .optional()
    .transform((value) => (value && value.length > 0 ? value : undefined)),
});

export const UpdateIpAllowlistSchema = z
  .object({
    enabled: z.boolean(),
    entries: z.array(IpAllowlistEntrySchema).max(IP_ALLOWLIST_MAX_ENTRIES),
  })
  .superRefine((value, ctx) => {
    if (value.enabled && value.entries.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Add at least one IP address or CIDR before enabling the allowlist',
        path: ['entries'],
      });
    }

    const seen = new Set<string>();

    value.entries.forEach((entry, index) => {
      if (seen.has(entry.cidr)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Duplicate IP address or CIDR',
          path: ['entries', index, 'cidr'],
        });

        return;
      }

      seen.add(entry.cidr);
    });
  });

export const isIpAllowed = ({
  ip,
  cidrs,
}: {
  ip: string;
  cidrs: readonly string[];
}): boolean => {
  const parsed = parseIpAddress(ip);

  if (!parsed) {
    return false;
  }

  return cidrs.some((cidr) => {
    try {
      const range = ipaddr.parseCIDR(cidr);

      return parsed.kind() === range[0].kind() && parsed.match(range);
    } catch {
      return false;
    }
  });
};

const IpAllowlistDeniedBodySchema = z.object({
  code: z.literal(IP_ALLOWLIST_DENIED_CODE),
  ip: z.string().nullish(),
});

const IpAllowlistDeniedDetailSchema = z.object({
  ip: z.string().nullable(),
});

export type IpAllowlistDenial = { ip: string | null };

export const parseIpAllowlistDenial = (
  body: unknown,
): IpAllowlistDenial | null => {
  const parsed = IpAllowlistDeniedBodySchema.safeParse(body);

  return parsed.success ? { ip: parsed.data.ip ?? null } : null;
};

export const notifyIpAllowlistDenied = (body: unknown): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const denial = parseIpAllowlistDenial(body);

  if (!denial) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(IP_ALLOWLIST_DENIED_EVENT, { detail: denial }),
  );
};

export const readIpAllowlistDeniedIp = (event: Event): string | null => {
  if (!(event instanceof CustomEvent)) {
    return null;
  }

  const parsed = IpAllowlistDeniedDetailSchema.safeParse(event.detail);

  return parsed.success ? parsed.data.ip : null;
};
